import { combineLatest, debounceTime } from 'rxjs';
import { focusedChrome$ } from 'scope/focusedChrome$';
import {
  type FocusedChrome,
  focusedChromeOperations,
} from 'scope/focusedChromeOperations';
import {
  ComponentTag,
  NavigatorItemData,
  PagesViewModel,
  PagesViewSettings,
  ReferenceLabel,
  ReferenceMap,
} from 'tabview/pagesView/types';
import {
  ReferenceTypeOption,
  TraversedReferenceTypeInfo,
  isTraversedReferenceType,
  matchSelectedReferenceType,
  getReferenceTypeInfo,
} from '@ardoq/settings-bar';
import { componentInterface } from '@ardoq/component-interface';
import { referenceInterface } from '@ardoq/reference-interface';
import type {
  GraphModelShape,
  ContextShape,
  WorkspaceAndModelLookup,
  RepresentationData,
} from '@ardoq/data-model';
import { dateRangeOperations } from '@ardoq/date-range';
import { APIReferenceAttributes } from '@ardoq/api-types';
import {
  buildReferenceFilterContext,
  type ReferenceFilterContext,
} from '@ardoq/filter-helpers';
import { toModelTypes, scopeDataOperations } from '@ardoq/scope-data';
import { workspaceAndModel$ } from 'streams/workspaceAndModel$';
import { references$ } from 'streams/references/references$';
import { tags$ } from 'streams/tags/tags$';
import { type StreamCollection } from 'streams/utils/streamUtils';
import { modelUpdateNotificationWithoutGraph$ } from 'modelInterface/modelUpdateNotification$';
import {
  APIComponentAttributes,
  APIComponentType,
  APIFieldAttributes,
  APITagAttributes,
  ArdoqId,
  ViewIds,
  LinkedComponent,
} from '@ardoq/api-types';
import { componentOperations, modelOperations } from '@ardoq/core';
import { components$, ComponentsState } from 'streams/components/components$';
import { models$, ModelsState } from 'streams/models/models$';
import { fields$, FieldsState, toFieldTypeKey } from 'streams/fields/fields$';
import { getReferenceTypesWithDefaults } from '@ardoq/data-model';
import { VIRTUAL_MODEL_ID } from '@ardoq/global-consts';
import {
  ExtractPayload,
  combineReducers,
  reducer,
  action$,
  streamReducer,
} from '@ardoq/rxbeach';
import {
  getComponentDisplayLabelById,
  getComponentFieldsOfWorkspace,
  getComponentFillById,
  getComponentPathById,
  getFieldsWithDateRanges,
} from './utils';
import { workspaceInterface } from '@ardoq/workspace-interface';
import {
  fieldsByWorkspaceId$,
  type FieldsByWorkspaceId,
} from 'streams/fields/fieldsByWorkspaceId$';
import { scrollToViewComponent } from 'tabview/pagesView/actions';
import { topVisibleItemChanged } from 'atomicComponents/InfiniteScroll/actions';
import { localeCompare, ARDOQ_DEFAULT_LOCALE, Locale } from '@ardoq/locale';
import { locale$ } from 'streams/locale$';
import {
  compareComponents,
  type ContextSort,
  isSameSet,
} from '@ardoq/common-helpers';
import { graphModel$ } from 'modelInterface/graphModel$';
import { uniqBy } from 'lodash';
import { ExtractStreamShape } from 'tabview/types';
import { isViewpointMode$ } from 'traversals/loadedGraph$';
import { currentUserPermissionContext$ } from 'streams/currentUserPermissions/currentUserPermissionContext$';
import {
  PermissionContext,
  permissionsOperations,
  resourcePermissionsOperations,
} from '@ardoq/access-control';
import { componentAccessControlOperation } from 'resourcePermissions/accessControlHelpers/component';
import { getFeatureContext } from 'streams/featureContext$';
import { FeatureContext } from '@ardoq/features';
import { activeScenario$ } from 'streams/activeScenario/activeScenario$';
import { CommonDropdownOptions } from '@ardoq/global-consts';
import { documentArchiveFolders$ } from 'streams/documentArchive/documentArchiveFolders$';
import { documentArchiveAttachments$ } from 'streams/documentArchive/documentArchiveAttachments$';
import { type DocumentArchiveAttachmentsState } from 'streams/documentArchive/types';
import { orgUsers$ } from 'streams/orgUsers/orgUsers$';
import { type OrgUsersStreamShape } from 'streams/orgUsers/types';
import { HasViewInstanceId } from '@ardoq/graph';
import { getWorkspaceModeViewSettings$ } from 'views/environmentals/viewSettings/workspaceModeViewSettings$';
import { getDefaultViewSettings } from 'views/environmentals/viewSettings/getDefaultViewSettings';
import { context$, emptyContext } from 'streams/context/context$';
import { i18nLocale$ } from 'streams/i18nLocale$';
import { DEFAULT_ARDOQ_I18NLOCALE, type I18nLocale } from '@ardoq/i18n';

const VIEW_ID = ViewIds.PAGESVIEW;

const getReferenceTypeAndWorkspaceName = (
  referenceTypeName: string,
  workspaceId: ArdoqId
) => {
  return `${referenceTypeName} (${workspaceInterface.getWorkspaceName(
    workspaceId
  )})`;
};

const resolveReferenceTypeInfo = (linkedComponent: LinkedComponent) =>
  getReferenceTypeInfo({
    referenceType: referenceInterface.getGlobalReferenceType(
      linkedComponent.referenceId
    ),
    linkedWorkspaceId:
      componentInterface.getWorkspaceId(linkedComponent.componentId) ?? '',
  });

const referenceIsVisible = (
  linkedComponent: LinkedComponent,
  includedReferenceTypes: ReferenceTypeOption[],
  isOutgoing: boolean
) => {
  return (
    (includedReferenceTypes.length === 1 &&
      includedReferenceTypes[0] === CommonDropdownOptions.ALL) ||
    includedReferenceTypes.find(
      includedReferenceType =>
        isTraversedReferenceType(includedReferenceType) &&
        matchSelectedReferenceType(
          { ...resolveReferenceTypeInfo(linkedComponent), isOutgoing },
          includedReferenceType
        )
    )
  );
};

const isLinkedReferenceIncluded = (
  linkedComponent: LinkedComponent,
  referencesById: Record<ArdoqId, APIReferenceAttributes>,
  ctx: ReferenceFilterContext
): boolean => {
  const reference = referencesById[linkedComponent.referenceId];
  return reference
    ? referenceInterface.isIncludedInContextByFilter(reference, ctx)
    : true;
};

const getReferenceMap = (
  componentIds: ArdoqId[],
  includedIncomingReferenceTypes: ReferenceTypeOption[],
  includedOutgoingReferenceTypes: ReferenceTypeOption[],
  graphModel: GraphModelShape,
  references: StreamCollection<APIReferenceAttributes>,
  ctx: ReferenceFilterContext
) =>
  new Map(
    componentIds.map(compId => [
      compId,
      {
        incoming: (graphModel.targetMap.get(compId) ?? [])
          .filter(
            linkedComponent =>
              isLinkedReferenceIncluded(
                linkedComponent,
                references.byId,
                ctx
              ) &&
              referenceIsVisible(
                linkedComponent,
                includedIncomingReferenceTypes,
                false
              )
          )
          .map(linkedComp => linkedComp.referenceId),
        outgoing: (graphModel.sourceMap.get(compId) ?? [])
          .filter(
            linkedComponent =>
              isLinkedReferenceIncluded(
                linkedComponent,
                references.byId,
                ctx
              ) &&
              referenceIsVisible(
                linkedComponent,
                includedOutgoingReferenceTypes,
                true
              )
          )
          .map(linkedComp => linkedComp.referenceId),
      },
    ])
  );

const getDescendantComponentIds = (
  workspaceId: ArdoqId | null,
  componentId: ArdoqId | null,
  components: ComponentsState,
  sort: ContextSort,
  locale: Locale
): ArdoqId[] => {
  const sortSiblings = (siblings: APIComponentAttributes[]) =>
    [...siblings].sort((a, b) => compareComponents(a, b, sort, locale));

  if (componentId) {
    const root = components.byId[componentId];
    if (!root) return [];
    const children = sortSiblings(
      components.childrenByParent.get(componentId) ?? []
    );
    return [
      componentId,
      ...children.flatMap(child =>
        getDescendantComponentIds(
          workspaceId,
          child._id,
          components,
          sort,
          locale
        )
      ),
    ];
  }
  if (workspaceId) {
    const roots = sortSiblings(
      componentOperations.getRootComponents(workspaceId, components.models)
    );
    return roots.flatMap(root =>
      getDescendantComponentIds(workspaceId, root._id, components, sort, locale)
    );
  }
  return [];
};

const getFields = (
  fieldsByWorkspaceId: FieldsByWorkspaceId,
  context: ContextShape
) =>
  getComponentFieldsOfWorkspace(fieldsByWorkspaceId[context.workspaceId] ?? []);

const getReferenceTypesWithWorkspaceNames = (
  componentIds: ArdoqId[],
  isOutgoing: boolean,
  locale: Locale
): TraversedReferenceTypeInfo[] => {
  const referenceTypes = uniqBy(
    uniqBy(
      componentIds.flatMap(
        isOutgoing
          ? componentInterface.getTargets
          : componentInterface.getSources
      ),
      'referenceId'
    ).map(resolveReferenceTypeInfo),
    ({ referenceTypeName, linkedWorkspaceId }) =>
      `${referenceTypeName}~~${linkedWorkspaceId}`
  );
  const duplicates = new Set(
    referenceTypes
      .filter(
        (a, index, array) =>
          array.findIndex(b => a.referenceTypeName === b.referenceTypeName) !==
          index
      )
      .map(({ referenceTypeName }) => referenceTypeName)
  );

  return referenceTypes
    .map(referenceType => ({
      ...referenceType,
      isOutgoing,
      displayName: duplicates.has(referenceType.referenceTypeName)
        ? getReferenceTypeAndWorkspaceName(
            referenceType.referenceTypeName,
            referenceType.linkedWorkspaceId
          )
        : referenceType.referenceTypeName,
    }))
    .sort((a, b) => localeCompare(a.displayName, b.displayName, locale));
};

const defaultViewSettings = getDefaultViewSettings(VIEW_ID, false);

const emptyState: PagesViewModel = {
  viewSettings: defaultViewSettings,
  context: emptyContext(),
  workspace: undefined,
  redactedFieldNames: new Set(),
  incomingReferenceTypes: [],
  outgoingReferenceTypes: [],
  componentIds: [],
  allComponentIds: [],
  filteredComponentIds: [],
  referenceMap: new Map(),
  referenceLabelById: new Map(),
  componentsById: {},
  usersById: {},
  attachmentsById: {},
  componentRepresentationById: new Map(),
  navigatorItemDataById: new Map(),
  workspaceFields: [],
  isViewpointMode: false,
  isScenarioMode: false,
  permissionContext: permissionsOperations.createEmptyPermissionContext(),
  canEditDescription: false,
  isReadOnlyVisualization: false,
  componentWriteAccessById: new Map(),
  componentFieldsById: new Map(),
  tagsByModelId: new Map(),
  componentPathById: new Map(),
  componentDisplayLabelById: new Map(),
  componentFillById: new Map(),
  i18nLocale: DEFAULT_ARDOQ_I18NLOCALE,
  locale: ARDOQ_DEFAULT_LOCALE,
};

interface ResetArgs {
  context: ContextShape;
  components: ComponentsState;
  references: StreamCollection<APIReferenceAttributes>;
  tags: StreamCollection<APITagAttributes>;
  workspaceAndModel: WorkspaceAndModelLookup;
  models: ModelsState;
  fields: FieldsState;
  viewSettings: PagesViewSettings;
  graphModel: GraphModelShape;
  isViewpointMode: boolean;
  isScenarioMode: boolean;
  permissionContext: PermissionContext;
  locale: Locale;
  i18nLocale: I18nLocale;
  fieldsByWorkspaceId: FieldsByWorkspaceId;
  orgUsers: OrgUsersStreamShape;
  attachments: DocumentArchiveAttachmentsState;
  focusedChrome: FocusedChrome;
}

const isComponentIncluded = (
  componentId: ArdoqId,
  components: ComponentsState,
  ctx: ReferenceFilterContext
): boolean => {
  const component = components.byId[componentId];
  return component
    ? componentInterface.isIncludedInContextByFilter(component, ctx)
    : true;
};

const componentOrDescendantsIncluded = (
  componentId: ArdoqId,
  components: ComponentsState,
  ctx: ReferenceFilterContext
): boolean =>
  isComponentIncluded(componentId, components, ctx) ||
  (components.childrenByParent.get(componentId) ?? []).some(child =>
    componentOrDescendantsIncluded(child._id, components, ctx)
  );
class ReferenceTypeCache {
  private result: TraversedReferenceTypeInfo[] = [];
  private ids: ArdoqId[] = [];
  private graphModel: GraphModelShape | null = null;
  constructor(private isOutgoing: boolean) {}

  get(ids: ArdoqId[], graphModel: GraphModelShape, locale: Locale) {
    if (graphModel === this.graphModel && isSameSet(ids, this.ids)) {
      return this.result;
    }
    this.result = getReferenceTypesWithWorkspaceNames(
      ids,
      this.isOutgoing,
      locale
    );
    this.ids = ids;
    this.graphModel = graphModel;
    return this.result;
  }
}

const outgoingReferenceTypesCache = new ReferenceTypeCache(true);
const incomingReferenceTypesCache = new ReferenceTypeCache(false);

/**
 * Resolves per-component write access here in the (lint-permitted) viewModel$
 * instead of letting the view reach into the model interface mid-render. This
 * mirrors what `componentInterface.hasWriteAccess` does under the hood —
 * `componentAccessControlOperation.canEditComponent` keyed on the component's
 * own `rootWorkspace` — using the `permissionContext` already on this stream.
 */
const getComponentWriteAccessById = (
  componentIds: ArdoqId[],
  components: ComponentsState,
  permissionContext: PermissionContext,
  featureContext: FeatureContext
): Map<ArdoqId, boolean> =>
  new Map(
    componentIds.flatMap((componentId): [ArdoqId, boolean][] => {
      const component = components.byId[componentId];
      if (!component) return [];
      return [
        [
          componentId,
          componentAccessControlOperation.canEditComponent({
            component,
            permissionContext,
            featureContext,
          }),
        ],
      ];
    })
  );

const getReferenceIds = (referenceMap: ReferenceMap): ArdoqId[] => [
  ...new Set(
    [...referenceMap.values()].flatMap(({ incoming, outgoing }) => [
      ...incoming,
      ...outgoing,
    ])
  ),
];

const getGlobalReferenceTypeName = (
  models: ModelsState,
  reference: APIReferenceAttributes
): string => {
  const model = models.byId[reference.model];
  if (!model) return '';
  if (reference.model === VIRTUAL_MODEL_ID) {
    return model.referenceTypes[reference.type]?.name ?? '';
  }
  return (
    modelOperations.getRefTypeName(
      getReferenceTypesWithDefaults(model),
      reference.type
    ) ?? ''
  );
};

const getReferenceFields = (
  fields: FieldsState,
  reference: APIReferenceAttributes
): APIFieldAttributes[] =>
  fields.byReferenceType.get(toFieldTypeKey(reference.model, reference.type)) ??
  [];

/**
 * Resolves each reference's type name, source/target endpoints, description and
 * fields here in the (lint-permitted) viewModel$ instead of letting the view
 * reach into the model interface mid-render. Type name + source/target were
 * already resolved eagerly by the view (the reference sort reads the type name
 * for every reference on each render). Description and fields were previously
 * resolved on collapsible-expand; resolving them upstream costs only a property
 * read (description) plus a date-range merge already loaded in the model — the
 * same eager date-range work `componentFieldsById` does for components. Mirrors
 * `componentWriteAccessById` / `componentFieldsById`.
 */
const getReferenceLabelById = (
  referenceMap: ReferenceMap,
  references: StreamCollection<APIReferenceAttributes>,
  models: ModelsState,
  fields: FieldsState
): Map<ArdoqId, ReferenceLabel> =>
  new Map(
    getReferenceIds(referenceMap).map(
      (referenceId): [ArdoqId, ReferenceLabel] => {
        const reference = references.byId[referenceId];
        if (!reference) {
          return [
            referenceId,
            {
              source: '',
              target: '',
              globalReferenceTypeName: '',
              description: '',
              fields: [],
              displayText: '',
              attributes: null,
            },
          ];
        }
        return [
          referenceId,
          {
            source: reference.source ?? '',
            target: reference.target ?? '',
            globalReferenceTypeName: getGlobalReferenceTypeName(
              models,
              reference
            ),
            description: reference.description ?? '',
            fields: dateRangeOperations.mergeDateTimeFieldsToDateRangeFields(
              getReferenceFields(fields, reference)
            ).fields,
            displayText: reference.displayText ?? '',
            attributes: reference,
          },
        ];
      }
    )
  );

const getComponentType = (
  models: ModelsState,
  component: APIComponentAttributes
): APIComponentType | null => {
  const model = models.byId[component.model];
  return model
    ? (modelOperations.getModelTypes(model).componentTypesById[
        component.typeId
      ] ?? null)
    : null;
};

/**
 * Resolves the representation data (icon / image) for every page and
 * reference-endpoint component here in the (lint-permitted) viewModel$ so the
 * view doesn't call `componentInterface.getRepresentationData` mid-render. Data
 * is already loaded in the model; mirrors `componentWriteAccessById`.
 */
const getComponentRepresentation = (
  models: ModelsState,
  component: APIComponentAttributes | undefined
): RepresentationData | null => {
  if (!component) return null;
  return componentOperations.getRepresentationData(
    component,
    getComponentType(models, component)
  );
};

const getComponentRepresentationById = (
  componentIds: ArdoqId[],
  referenceLabelById: Map<ArdoqId, ReferenceLabel>,
  components: ComponentsState,
  models: ModelsState
): Map<ArdoqId, RepresentationData | null> => {
  const representationComponentIds = new Set([
    ...componentIds,
    ...[...referenceLabelById.values()].flatMap(({ source, target }) => [
      source,
      target,
    ]),
  ]);
  return new Map(
    [...representationComponentIds].map(componentId => [
      componentId,
      getComponentRepresentation(models, components.byId[componentId]),
    ])
  );
};

const getComponentFieldsById = (
  componentIds: ArdoqId[],
  components: ComponentsState,
  fields: FieldsState
): Map<ArdoqId, APIFieldAttributes[]> =>
  new Map(
    componentIds.map(componentId => {
      const component = components.byId[componentId];
      const typeFields = component
        ? (fields.byComponentType.get(
            toFieldTypeKey(component.model, component.typeId)
          ) ?? [])
        : [];
      return [componentId, getFieldsWithDateRanges(typeFields)];
    })
  );

const getTagsByModelId = (
  modelIds: ArdoqId[],
  tags: StreamCollection<APITagAttributes>
): Map<ArdoqId, ComponentTag[]> =>
  new Map(
    modelIds.map(modelId => [
      modelId,
      tags.models
        .filter(
          tag =>
            tag.components?.includes(modelId) ||
            tag.references?.includes(modelId)
        )
        .map(tag => ({
          id: tag._id,
          name: tag.name,
          description: tag.description ?? '',
        })),
    ])
  );

const getTreeDepth = (
  componentId: ArdoqId | null,
  components: ComponentsState
): number => {
  if (!componentId) return 0;
  const parentId = components.byId[componentId]?.parent ?? null;
  return parentId ? getTreeDepth(parentId, components) + 1 : 0;
};

const getNavigatorItemDataById = (
  componentIds: ArdoqId[],
  components: ComponentsState,
  models: ModelsState
): Map<ArdoqId, NavigatorItemData> => {
  const componentTypesByModelId = new Map<
    ArdoqId,
    Record<ArdoqId, APIComponentType>
  >();
  const resolveComponentType = (
    component: APIComponentAttributes
  ): APIComponentType | null => {
    const model = models.byId[component.model];
    if (!model) return null;
    const componentTypesById =
      componentTypesByModelId.get(component.model) ??
      modelOperations.getModelTypes(model).componentTypesById;
    componentTypesByModelId.set(component.model, componentTypesById);
    return componentTypesById[component.typeId] ?? null;
  };
  return new Map(
    componentIds.flatMap((componentId): [ArdoqId, NavigatorItemData][] => {
      const component = components.byId[componentId];
      if (!component) return [];
      return [
        [
          componentId,
          {
            component,
            componentType: resolveComponentType(component),
            treeDepth: getTreeDepth(componentId, components),
          },
        ],
      ];
    })
  );
};

export const reset = ({
  context,
  components,
  references,
  tags,
  workspaceAndModel,
  models,
  fields,
  viewSettings,
  graphModel,
  isViewpointMode,
  isScenarioMode,
  permissionContext,
  locale,
  i18nLocale,
  fieldsByWorkspaceId,
  orgUsers,
  attachments,
  focusedChrome,
}: ResetArgs): PagesViewModel => {
  if (!context.workspaceId) {
    return { ...emptyState, i18nLocale };
  }

  const workspace = scopeDataOperations.getWorkspace(
    workspaceAndModel,
    context.workspaceId
  );
  if (!workspace) {
    return { ...emptyState, i18nLocale };
  }

  const typesByModelId = toModelTypes(
    Object.values(workspaceAndModel.modelsById)
  );
  const ctx = buildReferenceFilterContext({
    components: components.models,
    references: references.models,
    typesByModelId,
  });

  const allComponentIds = getDescendantComponentIds(
    context.workspaceId,
    context.componentId,
    components,
    context.sort,
    locale
  ).filter(componentId =>
    componentOrDescendantsIncluded(componentId, components, ctx)
  );

  const componentIds = allComponentIds.filter(componentId =>
    isComponentIncluded(componentId, components, ctx)
  );

  const componentIdsSet = new Set(componentIds);
  const filteredComponentIds = allComponentIds.filter(
    componentId => !componentIdsSet.has(componentId)
  );
  const workspaceFields = getFields(fieldsByWorkspaceId, context);
  const incomingReferenceTypes = incomingReferenceTypesCache.get(
    componentIds,
    graphModel,
    locale
  );
  const outgoingReferenceTypes = outgoingReferenceTypesCache.get(
    componentIds,
    graphModel,
    locale
  );

  const referenceMap = getReferenceMap(
    componentIds,
    viewSettings.includeIncomingReferenceTypes,
    viewSettings.includeOutgoingReferenceTypes,
    graphModel,
    references,
    ctx
  );
  const referenceLabelById = getReferenceLabelById(
    referenceMap,
    references,
    models,
    fields
  );
  const componentRepresentationById = getComponentRepresentationById(
    componentIds,
    referenceLabelById,
    components,
    models
  );

  const redactedFieldNames = new Set(
    permissionsOperations.redactedFieldsInWorkspace(
      permissionContext,
      context.workspaceId
    )
  );
  const isReadOnlyVisualization =
    focusedChromeOperations.isReadOnlyVisualization(focusedChrome);
  const canEditDescription =
    resourcePermissionsOperations.canEditWorkspace(
      permissionContext,
      context.workspaceId
    ) && !isReadOnlyVisualization;
  const componentWriteAccessById = isReadOnlyVisualization
    ? new Map<ArdoqId, boolean>()
    : getComponentWriteAccessById(
        componentIds,
        components,
        permissionContext,
        getFeatureContext()
      );
  const componentFieldsById = getComponentFieldsById(
    componentIds,
    components,
    fields
  );
  const tagsByModelId = getTagsByModelId(
    [...componentIds, ...getReferenceIds(referenceMap)],
    tags
  );
  const componentPathById = getComponentPathById(
    componentIds,
    referenceLabelById,
    components,
    workspaceAndModel
  );
  const componentDisplayLabelById = getComponentDisplayLabelById(componentIds);
  const componentFillById = getComponentFillById(componentIds);
  const navigatorItemDataById = getNavigatorItemDataById(
    allComponentIds,
    components,
    models
  );

  return {
    viewSettings,
    context,
    workspace,
    redactedFieldNames,
    workspaceFields,
    componentIds,
    allComponentIds,
    filteredComponentIds,
    incomingReferenceTypes,
    outgoingReferenceTypes,
    referenceMap,
    referenceLabelById,
    componentsById: components.byId,
    usersById: orgUsers.byId,
    attachmentsById: attachments.byId,
    componentRepresentationById,
    navigatorItemDataById,
    isViewpointMode,
    isScenarioMode,
    permissionContext,
    canEditDescription,
    isReadOnlyVisualization,
    componentWriteAccessById,
    componentFieldsById,
    tagsByModelId,
    componentPathById,
    componentDisplayLabelById,
    componentFillById,
    i18nLocale,
    locale,
  };
};

const handleScrollToViewComponent = (
  state: PagesViewModel,
  { componentId }: ExtractPayload<typeof scrollToViewComponent>
): PagesViewModel => {
  return {
    ...state,
    scrollToViewComponent: componentId,
  };
};

const handleTopVisibleItemChanged = (
  state: PagesViewModel,
  { itemId }: ExtractPayload<typeof topVisibleItemChanged>
): PagesViewModel => ({
  ...state,
  topVisibleComponentId: itemId,
});

export const getViewModel$ = ({ viewInstanceId }: HasViewInstanceId) => {
  const reset$ = combineLatest([
    getWorkspaceModeViewSettings$(ViewIds.PAGESVIEW, viewInstanceId),
    context$,
    components$,
    references$,
    tags$,
    workspaceAndModel$,
    models$,
    fields$,
    graphModel$,
    isViewpointMode$,
    activeScenario$,
    currentUserPermissionContext$,
    locale$,
    i18nLocale$,
    fieldsByWorkspaceId$,
    orgUsers$,
    documentArchiveAttachments$,
    focusedChrome$,
    modelUpdateNotificationWithoutGraph$,
    documentArchiveFolders$,
  ]);

  const handleReset = (
    _: PagesViewModel,
    [
      viewSettings,
      context,
      components,
      references,
      tags,
      workspaceAndModel,
      models,
      fields,
      graphModel,
      { isViewpointMode },
      { isScenarioMode },
      permissionContext,
      locale,
      i18nLocale,
      fieldsByWorkspaceId,
      orgUsers,
      attachments,
      focusedChrome,
    ]: ExtractStreamShape<typeof reset$>
  ) =>
    reset({
      viewSettings,
      context,
      components,
      references,
      tags,
      workspaceAndModel,
      models,
      fields,
      graphModel,
      isViewpointMode,
      isScenarioMode,
      permissionContext,
      locale,
      i18nLocale,
      fieldsByWorkspaceId,
      orgUsers,
      attachments,
      focusedChrome,
    });

  return action$.pipe(
    combineReducers<PagesViewModel>(emptyState, [
      streamReducer(reset$, handleReset),
      reducer(scrollToViewComponent, handleScrollToViewComponent),
      reducer(topVisibleItemChanged, handleTopVisibleItemChanged),
    ]),
    // avoiding repeated unnecessary values
    // due to interconnected viewModel, activeFilter streams
    // helps with fewer rerenderings of the connected React component
    debounceTime(100)
  );
};
